import { GroupByDateFormat } from "@/types/insights/groupByDateFormat"
import { SearchClauseParameters, View } from "@/types/view"
import { CaseColumn } from "../caseColumns"
import { InsightCalculation, CalculationType, CalculationSortItemType, InsightCalculationTotal, InsightCalculationResult } from "./calculation"
import { InsightSearch, SearchField, SearchColumn } from "./search"
import { DataType, Field } from "@/types"
import { PageQueryParams } from "../paginatedRequest"
import { FieldDataTypes } from "../fieldTypes"

export { InsightCalculation, CalculationType, CalculationSortItemType, InsightCalculationTotal, InsightCalculationResult }
export { InsightSearch, SearchField, SearchColumn }

export { Operator, operatorLabels, operatorsByFieldType, dataTypeTests } from "./operators"
export { DataType, dataTypeByFieldType } from "../dataType"
export { LabelFormat } from "./labelFormat"
export { FormattingRule } from "./formattingRule"
export { GroupByDateFormat } from "./groupByDateFormat"

export interface Insight extends View, PageQueryParams {
	displayColumns?: any [];
	defaultView?: string | null

	lastUpdated?: Date;

	calculations: InsightCalculation[]
	calculationTotals?: InsightCalculationTotal[]
	calculationSort?: string[] | null

	groupBy?: string | null
	groupByDateFormat?: GroupByDateFormat | null

	groupingOptions?: { [key: string]: string }

	showCalculatedDateRange?: boolean | null
	showCalculatedUserStatus?: boolean | null

	includeCalculationsFrom?: string[] | null

	sortByDataType?: GroupByDateFormat | DataType | null
	sortByDescending?: boolean

	dynamicParameters?: DynamicParameter[] | null
	includeGlobalParameters?: string[] | null

	includeForecast?: boolean
	forecastBy?: string | null

	defaultTimeZone?: string | null
	tableId?: string | null;
}

export interface InsightSummary {
	includeGlobalParameters: any
    id: string
    tenantId: string
    title: string
    folder: string
    teams: any[]
	groupBy: string | null
	hasCalculations: boolean
}

export interface InsightDetails extends Insight {
	calculations: InsightCalculation[]
	displayColumns?: CaseColumn[]
	calculationSort: string[] | null
}

export interface DynamicParameter {
	dataType: DataType
	key: string
	name: string
	dataSourceId: string | null
	passValueToParameter?: string[]

	defaultValue?: any
	searchClauseParameters?: SearchClauseParameters | null
	dynamicValue?: boolean
}

export interface DynamicParameterValue {
	dataType: DataType
	key: string
	value: any
	dynamicValue: boolean
	passValueToParameter?: string[]
	searchClauseParameters?: SearchClauseParameters | null
	isAltered?: boolean
}

export const editableFieldTypes = new Set<string>([
	FieldDataTypes.Text, FieldDataTypes.TextArea, FieldDataTypes.Checkbox, FieldDataTypes.Number, FieldDataTypes.Dollar, FieldDataTypes.Date,
	FieldDataTypes.DateTime, FieldDataTypes.UserRole, FieldDataTypes.List, FieldDataTypes.ListMultiple, FieldDataTypes.ExternalLink, FieldDataTypes.ListData
])

export enum InsightViewType {
	Table = "table",
	HeatMap = "HeatMap",
	CalculationTable = "CalculationTable",
	ColumnChart = "ColumnChart",
	BarChart = "BarChart",
	ComboChart = "ComboChart",
	PieChart = "PieChart",
	LineChart = "LineChart"
}

export const insightViews = [
	{
		key: "table",
		label: "Table View",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => true
	},
	{
		key: "HeatMap",
		label: "Heat Map",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => insight.columns.some((column: string) => {
			var columnField: any
			if (usingDashboard) {
				columnField = (fields as Field[]).find(f => column === "fields." + f.id)
			} else {
				columnField = (fields as CaseColumn[]).find(f => column === "fields." + f.fieldId)
			}
			return columnField && columnField.dataType === "map-marker"
		})
	},
	{
		key: "CalculationTable",
		label: "Calculation Table",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => insight.groupBy && calculations.length > 0
	},
	{
		key: "ColumnChart",
		label: "Bar Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	},
	{
		key: "BarChart",
		label: "Horizontal Bar Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: any) => calculations.length > 0 && usingDashboard
	},
	{
		key: "ComboChart",
		label: "Combo Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	},
	{
		key: "PieChart",
		label: "Pie Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0 && calculations[0].groupBy === null
	},
	{
		key: "LineChart",
		label: "Line Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	}
]

export const createDefaultInsight = (): Insight => {
	return {
		id: undefined,
		clauses: [],
		groupBy: "",
		calculations: [],
		columns: [
			"title",
			"templateId"
		],
		title: "",
		folder: "",
		teams: [],
		viewTeams: [],
		sortBy: "",
		groupByDataType: null,
		groupByOption: null,
		tenantId: "",
		calculationTotals: [],
		sortByDescending: false,
		page: 1,
		pageSize: 20
	}
}
